import React, { PropsWithChildren, useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { usePrevious } from "react-use";
import { Actions, DockLocation } from "flexlayout-react";
import mapboxgl, { ScaleControl } from "mapbox-gl";

import { multiProjectWidgets } from "fond/layout/defaultLayout";
import { LayoutContext } from "fond/layout/LayoutProvider";
import Footer from "fond/map/Footer";
import { MapCreator, refreshTileSource, setRequestTransformations } from "fond/map/Map";
import { MapListener } from "fond/map/MapListener";
import { MapContext } from "fond/map/MapProvider";
import { loadMap, unloadMap } from "fond/map/redux";
import Ruler from "fond/map/Ruler";
import { getMapStyle } from "fond/map/styles";
import { getCurrentProject } from "fond/project";
import { unselectFeature, updateProjectView } from "fond/project/redux";
import store from "fond/store";
import * as turf from "fond/turf";
import { ErrorEventProps, Store, SystemOfMeasurement, View, ViewLocation, Widget } from "fond/types";
import { BBox } from "fond/types/geometry";
import { isValidBoundingBox } from "fond/utils";
import { useAppDispatch } from "fond/utils/hooks";

import { useFeatureHandler } from "./useFeatureHandler";

import { Container } from "../../map/BaseMap.styles";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_KEY || "";

interface IProps extends PropsWithChildren {
  showFooter?: boolean;
  mapContent: JSX.Element;
  /**
   * Whether to persist the view changes to the store/localstorage
   * It's useful to disable this when you want to load the zoomed-to-design view by default for smaller maps without
   * affecting the view stored against the project for the current user.
   */
  persistViewChange?: boolean;
}

const Map: React.FC<IProps> = ({ children, mapContent, showFooter = true, persistViewChange = true }: IProps) => {
  const dispatch = useAppDispatch();
  const { map, setMap, mapStyle: style } = useContext(MapContext);
  const layout = useContext(LayoutContext);
  const [distance, setDistance] = useState<string | undefined>();
  const containerRef = useRef<HTMLDivElement>(null);
  const project = useSelector((state: Store) => getCurrentProject(state.project));
  const selectedFeatureId = useSelector((state: Store) => state.project.selectedFeature?.featureProperties.id);
  const prevSelectedFeatureId = usePrevious(selectedFeatureId);
  let scaleControl = useRef<ScaleControl>();
  useFeatureHandler(map);

  /**
   * We only access the view.location value on mount as we don't
   * want to trigger a component render every time the map changes
   * zoom or positioning.
   *
   * We ignore any changes to view.location after mount.
   */
  let location: ViewLocation | undefined;
  useEffect(() => {
    if (project) {
      location = store.getState().project.projects[project.ID]?.view.location;
    }
  }, []);

  let mapListener: MapListener | null = null;

  useEffect(() => {
    return () => {
      setMap(undefined);
      mapListener = null;
      dispatch(unloadMap());
    };
  }, []);

  /**
   * If a feature is selected, add the properties widget to the layout
   * (if it doesn't already exist) and select it.
   */
  useEffect(() => {
    if (layout?.model) {
      const { model } = layout;
      const propertiesWidget = model.getNodeById(Widget.CityProperties);
      const legendParentId = model.getNodeById("legend")?.getParent()?.getId();

      // If the properties widget closes deselect the feature to avoid an loop
      // where it will instantly get added back if the feature is still selected.
      if (propertiesWidget) {
        propertiesWidget.setEventListener("close", () => {
          dispatch(unselectFeature());
        });
      }

      if (selectedFeatureId && selectedFeatureId !== prevSelectedFeatureId) {
        if (!propertiesWidget && legendParentId) {
          model.doAction(Actions.addNode(multiProjectWidgets.cityProperties, legendParentId, DockLocation.BOTTOM, 0, true));
        }
        if (!propertiesWidget?.isVisible()) {
          model.doAction(Actions.selectTab(Widget.CityProperties));
        }
      }
    }
  }, [dispatch, layout, prevSelectedFeatureId, selectedFeatureId]);

  /**
   * Once the container is rendered create the Map
   */
  useEffect(() => {
    async function makeNewMap() {
      // Once the container is rendered create the Map
      if (containerRef.current && project) {
        let bbox: BBox | undefined;
        if (
          location?.camera == null &&
          location?.bbox != null &&
          // Be careful we don't crash Mapbox by passing it out-of-range coordinates.
          isValidBoundingBox(location?.bbox)
        ) {
          bbox = location.bbox;
        }
        const baseStyle = getMapStyle(style);
        const newMap = await MapCreator.createMap({
          container: containerRef.current,
          view: location?.camera,
          bbox,
          style: baseStyle.url,
        });
        // if we are not persisting view change load the zoomed-to-design view by default
        if (!persistViewChange && project?.BoundingBox && isValidBoundingBox(project.BoundingBox)) {
          newMap.fitBounds(project.BoundingBox, { duration: 0, padding: 10 });
        }
        newMap.dragRotate.disable();
        newMap.touchZoomRotate.disableRotation();
        newMap.on("load", () => {
          setMap(newMap);
          // Our Selenium tests assume that the Map instance is available at `window.map`.
          window.map = newMap;
        });

        newMap.on("error", async ({ error, source, sourceId }: ErrorEventProps) => {
          // Refresh the token and tiles if this is a vector tile authorisation error
          if (source?.type === "vector" && error?.status && [400, 401].includes(error.status)) {
            await setRequestTransformations(newMap);
            refreshTileSource({ map: newMap, sourceId, source });
          }
        });
      }
    }

    makeNewMap();
  }, [containerRef, project]);

  /**
   * Handles the changing of the map view
   */
  const handleViewChange = (newView: View) => {
    if (project?.ID) {
      dispatch(updateProjectView(project.ID, newView));
    }
  };

  /**
   * Monitor the map for initialisation
   */
  useEffect(() => {
    // Add user position marker after the map is loaded
    const addSourceForUserPosition = () => {
      map?.addSource("location-point", {
        type: "geojson",
        data: {
          type: "Feature",
          properties: {},
          geometry: {
            type: "Point",
            coordinates: [],
          },
        },
      });
      map?.addSource("location-area", {
        type: "geojson",
        data: turf.circle([0, 0], 1, {}),
      });
    };

    if (map) {
      dispatch(loadMap(map));
      if (persistViewChange) {
        mapListener = new MapListener(map, handleViewChange);
      }

      if (!map.getSource("location-point") && !map.getSource("location-area")) {
        addSourceForUserPosition();
      }

      map.on("styledataloading", () => {
        map.once("styledata", () => {
          addSourceForUserPosition();
        });
      });

      // Once map has loaded we can add any required controls
      if (showFooter) setupScaleControl(project?.SystemOfMeasurement);
    }

    return () => {
      map?.off("styledataloading", addSourceForUserPosition);
    };
  }, [map]);

  /**
   * Adds & Removes the scale control to the map based on the current
   * System of Measurements
   */
  const setupScaleControl = (unit: SystemOfMeasurement = "imperial") => {
    if (map) {
      if (scaleControl.current != null && map.hasControl(scaleControl.current)) {
        map.removeControl(scaleControl.current);
      }

      const newScale = new ScaleControl({ maxWidth: 500, unit });
      map.addControl(newScale, "bottom-right");
      scaleControl.current = newScale;
    }
  };

  return (
    <Container ref={containerRef} data-testid="map-container" id="mapContainer">
      {map && (
        <>
          {children}
          {showFooter && (
            <>
              <Footer distance={distance} />
              <Ruler onChange={setDistance} />
            </>
          )}
          {mapContent}
        </>
      )}
    </Container>
  );
};

export default Map;
